import React from "react";
import styled from "styled-components";
import Page from "../../components/Page";
import Async from "../../components/Async";
import { Button, ColoredButton } from "../../components/buttons";
import api from "../../services/api";
import toasts from "../../services/toasts";

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const Card = styled.div`
  background: var(--button-bg);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: var(--button-shadow);
  padding: 18px;
`;

const CardTitle = styled.h4`
  margin: 0 0 14px;
`;

const Note = styled.p`
  margin: 0 0 16px;
  opacity: 0.75;
  line-height: 1.45;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--app-bg);
  color: var(--text-color);
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--app-bg);
  color: var(--text-color);
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const SummaryGrid = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  margin-bottom: 18px;
`;

const SummaryItem = styled.div`
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 10px 12px;
  background: var(--app-bg);
`;

const SourceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SourceItem = styled.div`
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 14px;
  background: var(--app-bg);
`;

const SourceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 8px;
`;

const SourceTitle = styled.div`
  font-weight: 700;
`;

const Meta = styled.div`
  font-size: 13px;
  opacity: 0.75;
  line-height: 1.5;
`;

const SplitRow = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(220px, 1fr) minmax(240px, 1fr) auto;
  align-items: end;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const EmptyState = styled.div`
  padding: 24px 0;
  opacity: 0.75;
`;

const loadCurzonMappings = () => api.showtimes.curzon.listMappings();
const loadPicturehouseMappings = () => api.showtimes.picturehouse.listMappings();

const SOURCE_CONFIG = {
  curzon: {
    label: "Curzon",
    note:
      "Match each Curzon venue to the correct Playground cinema, then import the current Curzon snapshot into the database.",
    importLabel: "Import Curzon snapshot now"
  },
  picturehouse: {
    label: "Picturehouse",
    note:
      "Match each Picturehouse venue to the correct Playground cinema, then import the current Picturehouse snapshot into the database.",
    importLabel: "Import Picturehouse snapshot now"
  }
};

const getErrorMessage = error => {
  if (error && error.response && error.response.data) {
    return error.response.data.message || error.response.data.error || error.toString();
  }
  return error.toString();
};

const formatDateTime = value => {
  if (!value) {
    return "Not imported yet";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString();
};

const formatDate = value => {
  if (!value) {
    return "Unknown";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString();
};

class Showtimes extends React.Component {
  state = {
    reload: false,
    selections: {
      curzon: {},
      picturehouse: {}
    },
    savingSlugs: {
      curzon: null,
      picturehouse: null
    },
    importingSnapshots: {
      curzon: false,
      picturehouse: false
    }
  };

  refresh = () => {
    this.setState(state => ({ reload: !state.reload }));
  };

  setSelection = (source, slug, placeId) => {
    this.setState(state => ({
      selections: {
        ...state.selections,
        [source]: {
          ...(state.selections[source] || {}),
          [slug]: placeId
        }
      }
    }));
  };

  saveMapping = async (source, slug, fallbackPlaceId = "") => {
    const sourceLabel = SOURCE_CONFIG[source].label;
    const selectedValue =
      (this.state.selections[source] && this.state.selections[source][slug]) ||
      fallbackPlaceId ||
      "";

    if (!selectedValue) {
      toasts.error(`Choose the Playground cinema that matches this ${sourceLabel} venue.`);
      return;
    }

    this.setState(state => ({
      savingSlugs: {
        ...state.savingSlugs,
        [source]: slug
      }
    }));

    try {
      const result = await api.showtimes[source].saveMapping({
        slug,
        place_id: Number(selectedValue),
        is_active: true
      });

      this.setState(state => ({
        selections: {
          ...state.selections,
          [source]: {
            ...(state.selections[source] || {}),
            [slug]: String(result.place_id)
          }
        }
      }));

      toasts.success(`Mapped ${result.venue_name} to ${result.place_name}.`);
      this.refresh();
    } catch (error) {
      toasts.error(getErrorMessage(error));
    } finally {
      this.setState(state => ({
        savingSlugs: {
          ...state.savingSlugs,
          [source]: null
        }
      }));
    }
  };

  importSnapshot = async source => {
    const sourceLabel = SOURCE_CONFIG[source].label;

    this.setState(state => ({
      importingSnapshots: {
        ...state.importingSnapshots,
        [source]: true
      }
    }));

    try {
      const result = await api.showtimes[source].importSnapshot({});
      toasts.success(
        `Imported ${result.totals.saved_showtimes} ${sourceLabel} showtimes across ${result.totals.venues} venues.`
      );
      this.refresh();
    } catch (error) {
      toasts.error(getErrorMessage(error));
    } finally {
      this.setState(state => ({
        importingSnapshots: {
          ...state.importingSnapshots,
          [source]: false
        }
      }));
    }
  };

  renderSourceSection = (source, payload) => {
    const config = SOURCE_CONFIG[source];
    const venues = (payload && payload.venues) || [];
    const candidates = (payload && payload.candidates) || [];
    const statusSummary = (payload && payload.status_summary) || {};
    const sourceSelections = this.state.selections[source] || {};
    const savingSlug = this.state.savingSlugs[source];
    const importingSnapshot = this.state.importingSnapshots[source];

    if (!venues.length) {
      return (
        <Card>
          <CardTitle>{config.label} Matching</CardTitle>
          <EmptyState>No {config.label} venues are configured yet.</EmptyState>
        </Card>
      );
    }

    return (
      <Card>
        <CardTitle>{config.label} Matching</CardTitle>
        <Note>{config.note}</Note>

        <SummaryGrid>
          <SummaryItem>
            <strong>Mapped Venues</strong>
            <Meta>
              {statusSummary.mapped_venue_count || 0} of {venues.length}
            </Meta>
          </SummaryItem>
          <SummaryItem>
            <strong>Imported Venues</strong>
            <Meta>{statusSummary.imported_venue_count || 0} with stored showtimes</Meta>
          </SummaryItem>
          <SummaryItem>
            <strong>Upcoming Showtimes</strong>
            <Meta>{statusSummary.total_upcoming_showtime_count || 0} rows in DB</Meta>
          </SummaryItem>
          <SummaryItem>
            <strong>Coverage Through</strong>
            <Meta>{formatDate(statusSummary.last_show_date)}</Meta>
          </SummaryItem>
          <SummaryItem>
            <strong>Last Import</strong>
            <Meta>{formatDateTime(statusSummary.last_imported_at)}</Meta>
          </SummaryItem>
          <SummaryItem>
            <strong>Snapshot Fetched</strong>
            <Meta>{formatDateTime(payload && payload.snapshot_fetched_at)}</Meta>
          </SummaryItem>
        </SummaryGrid>

        <ButtonRow>
          <ColoredButton
            type="button"
            onClick={() => this.importSnapshot(source)}
            disabled={importingSnapshot}
          >
            {importingSnapshot ? "Importing snapshot..." : config.importLabel}
          </ColoredButton>
        </ButtonRow>

        <SourceList>
          {venues.map(venue => {
            const selectedValue =
              sourceSelections[venue.slug] !== undefined
                ? sourceSelections[venue.slug]
                : venue.mapped_place_id
                  ? String(venue.mapped_place_id)
                  : "";
            const selectedCandidate = candidates.find(
              candidate => String(candidate.place_id) === String(selectedValue)
            );
            const addressValue =
              (selectedCandidate && selectedCandidate.address) ||
              venue.mapped_place_address ||
              "";
            const status = venue.status || null;

            return (
              <SourceItem key={`${source}-${venue.slug}`}>
                <SourceHeader>
                  <div>
                    <SourceTitle>{venue.venue_name}</SourceTitle>
                    <Meta>ID {venue.site_id} - {venue.query}</Meta>
                    {venue.mapped_place_name ? (
                      <Meta>
                        Currently mapped to {venue.mapped_place_name}
                        {venue.mapped_place_id ? ` (#${venue.mapped_place_id})` : ""}
                      </Meta>
                    ) : (
                      <Meta>No place selected yet.</Meta>
                    )}
                    {status && status.has_imported_showtimes ? (
                      <React.Fragment>
                        <Meta>
                          {status.upcoming_showtime_count || 0} upcoming showtimes,{" "}
                          {status.movie_count || 0} movies, {status.stored_showtime_count || 0} stored total
                        </Meta>
                        <Meta>
                          Next show {formatDate(status.next_show_date)}. Coverage through{" "}
                          {formatDate(status.last_show_date)}.
                        </Meta>
                        <Meta>Last imported {formatDateTime(status.last_imported_at)}</Meta>
                      </React.Fragment>
                    ) : venue.mapped_place_name ? (
                      <Meta>No {config.label} showtimes imported for this mapped place yet.</Meta>
                    ) : null}
                  </div>
                </SourceHeader>

                <SplitRow>
                  <div>
                    <Label>Playground place</Label>
                    <Select
                      value={selectedValue}
                      onChange={event => this.setSelection(source, venue.slug, event.target.value)}
                    >
                      <option value="">Choose a cinema place</option>
                      {candidates.map(candidate => {
                        const mappedElsewhere =
                          candidate.mapping_slug && candidate.mapping_slug !== venue.slug;
                        const label = `${candidate.place_name} (#${candidate.place_id})${
                          mappedElsewhere
                            ? ` - already mapped to ${candidate.mapping_slug}`
                            : ""
                        }`;

                        return (
                          <option
                            key={`${source}-${candidate.place_id}`}
                            value={candidate.place_id}
                            disabled={mappedElsewhere}
                          >
                            {label}
                          </option>
                        );
                      })}
                    </Select>
                  </div>

                  <div>
                    <Label>Address</Label>
                    <Input
                      value={addressValue}
                      placeholder="Select a cinema place to preview its address"
                      readOnly
                    />
                  </div>

                  <Button
                    type="button"
                    onClick={() => this.saveMapping(source, venue.slug, selectedValue)}
                    disabled={!selectedValue || savingSlug === venue.slug}
                  >
                    {savingSlug === venue.slug ? "Saving..." : "Save match"}
                  </Button>
                </SplitRow>
              </SourceItem>
            );
          })}
        </SourceList>
      </Card>
    );
  };

  render() {
    const { reload } = this.state;

    return (
      <Page title="Showtimes">
        <Stack>
          <Async fetchProps={loadCurzonMappings} reload={reload}>
            {payload => this.renderSourceSection("curzon", payload)}
          </Async>
          <Async fetchProps={loadPicturehouseMappings} reload={reload}>
            {payload => this.renderSourceSection("picturehouse", payload)}
          </Async>
        </Stack>
      </Page>
    );
  }
}

export default Showtimes;
